import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Container } from 'reactstrap';
import { Box } from '@mui/material';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import useSyndicationAccess from '../common/useSyndicationAccess';
import { getAvailableSyndicationsPage } from '../../../shared/services/apiGateway';

const PAGE_UNAVAILABLE_MESSAGE = 'Please check back later for more Live Deals.';
const DASHBOARD_REDIRECT_DELAY_MS = 3000;
const IFRAME_ANALYTICS_MESSAGE_TYPE = 'availableSyndications.analytics.click';
const IFRAME_HEIGHT_MESSAGE_TYPE = 'availableSyndications.layout.height';
const IFRAME_MEASURE_MESSAGE_TYPE = 'availableSyndications.layout.measure';
const IFRAME_ANALYTICS_EVENT_NAME = 'available_syndications_iframe_click';
const GTM_SCRIPT_ID = 'av-cookie-consent-gtm-script';
const CONSENT_COOKIE_KEY = 'av_cookie_consent';
const MAX_ANALYTICS_TEXT_LENGTH = 120;
const MAX_ANALYTICS_HREF_LENGTH = 500;
const IFRAME_ANALYTICS_SCRIPT = `
<script>
(function () {
  function findClickableElement(target) {
    if (!target || typeof target.closest !== 'function') {
      return null;
    }

    return target.closest('a, button, [role="button"]');
  }

  document.addEventListener('click', function (event) {
    var clickableElement = findClickableElement(event.target);
    if (!clickableElement) {
      return;
    }

    window.parent.postMessage({
      type: '${IFRAME_ANALYTICS_MESSAGE_TYPE}',
      tagName: clickableElement.tagName || '',
      href: clickableElement.href || clickableElement.getAttribute('href') || '',
      text: clickableElement.innerText || clickableElement.textContent || ''
    }, '*');
  }, true);
}());
</script>
`;
const IFRAME_HEIGHT_SCRIPT = `
<script>
(function () {
  var animationFrame = null;
  var lastHeight = 0;
  var requestFrame = window.requestAnimationFrame || function (callback) {
    return window.setTimeout(callback, 16);
  };

  function getDocumentHeight() {
    var body = document.body;
    var documentElement = document.documentElement;
    if (!body) {
      return documentElement ? documentElement.scrollHeight : 0;
    }

    var height = 0;
    Array.prototype.forEach.call(body.getElementsByTagName('*'), function (element) {
      var rect = element.getBoundingClientRect();

      if (!rect || (!rect.width && !rect.height)) {
        return;
      }

      var style = window.getComputedStyle ? window.getComputedStyle(element) : null;
      var marginBottom = style ? parseFloat(style.marginBottom) || 0 : 0;
      height = Math.max(height, Math.ceil(rect.bottom + window.pageYOffset + marginBottom));
    });

    if (height > 0) {
      return height;
    }

    return Math.max(
      body.scrollHeight || 0,
      body.offsetHeight || 0,
      documentElement ? documentElement.scrollHeight : 0,
      documentElement ? documentElement.offsetHeight : 0
    );
  }

  function reportHeight() {
    if (animationFrame) {
      return;
    }

    animationFrame = requestFrame(function () {
      animationFrame = null;

      var height = Math.ceil(getDocumentHeight());
      if (typeof height !== 'number' || !isFinite(height) || height <= 0 || height === lastHeight) {
        return;
      }

      lastHeight = height;
      window.parent.postMessage({
        type: '${IFRAME_HEIGHT_MESSAGE_TYPE}',
        height: height
      }, '*');
    });
  }

  window.addEventListener('load', reportHeight);
  window.addEventListener('resize', reportHeight);
  window.addEventListener('message', function (event) {
    var message = event.data;
    if (!message || message.type !== '${IFRAME_MEASURE_MESSAGE_TYPE}') {
      return;
    }

    reportHeight();
  });

  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(reportHeight).catch(function () {});
  }

  if (window.MutationObserver && document.body) {
    new MutationObserver(reportHeight).observe(document.body, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  if (window.ResizeObserver) {
    var resizeObserver = new ResizeObserver(reportHeight);

    if (document.body) {
      resizeObserver.observe(document.body);
    }

    if (document.documentElement) {
      resizeObserver.observe(document.documentElement);
    }
  }

  [0, 100, 500, 1000, 2000].forEach(function (delay) {
    window.setTimeout(reportHeight, delay);
  });
}());
</script>
`;

const readCookie = (cookieName) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const matchingCookie = document.cookie
    .split(';')
    .map((cookiePart) => cookiePart.trim())
    .find((cookiePart) => cookiePart.startsWith(`${cookieName}=`));

  return matchingCookie ? matchingCookie.slice(cookieName.length + 1) : null;
};

const hasRejectedTrackingConsent = () => {
  const rawConsentCookie = readCookie(CONSENT_COOKIE_KEY);
  if (!rawConsentCookie) {
    return false;
  }

  try {
    const parsedConsentCookie = JSON.parse(decodeURIComponent(rawConsentCookie));
    return parsedConsentCookie?.status === 'rejected';
  } catch (error) {
    return false;
  }
};

const canPushIframeAnalytics = () => (
  typeof document !== 'undefined'
  && typeof window !== 'undefined'
  && Array.isArray(window.dataLayer)
  && !!document.getElementById(GTM_SCRIPT_ID)
  && !hasRejectedTrackingConsent()
);

const normalizeAnalyticsText = (value, maxLength) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/\s+/g, ' ').trim().slice(0, maxLength);
};

const normalizeAnalyticsHref = (value) => {
  const normalizedValue = normalizeAnalyticsText(value, MAX_ANALYTICS_HREF_LENGTH);
  if (!normalizedValue) {
    return '';
  }

  try {
    const url = new URL(normalizedValue, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return '';
    }

    return `${url.origin}${url.pathname}`.slice(0, MAX_ANALYTICS_HREF_LENGTH);
  } catch (error) {
    return normalizedValue.split(/[?#]/)[0].slice(0, MAX_ANALYTICS_HREF_LENGTH);
  }
};

const addIframeScripts = (html) => `${html}${IFRAME_ANALYTICS_SCRIPT}${IFRAME_HEIGHT_SCRIPT}`;

const getSyndicateSignupUrl = () => {
  if (!process.env.APP_AV_VC_PATH) {
    throw new Error('APP_AV_VC_PATH is required to redirect to syndicate signup.');
  }

  return `${process.env.APP_AV_VC_PATH}/syndicate-signup`;
};

const getDocumentHeight = (document) => {
  const { body, documentElement } = document;
  const documentWindow = document.defaultView || window;
  if (!body) {
    return documentElement?.scrollHeight || 0;
  }

  let height = 0;
  Array.from(body.getElementsByTagName('*')).forEach((element) => {
    const rect = element.getBoundingClientRect();

    if (!rect || (!rect.width && !rect.height)) {
      return;
    }

    const style = documentWindow.getComputedStyle ? documentWindow.getComputedStyle(element) : null;
    const marginBottom = style ? parseFloat(style.marginBottom) || 0 : 0;
    height = Math.max(height, Math.ceil(rect.bottom + documentWindow.pageYOffset + marginBottom));
  });

  if (height > 0) {
    return height;
  }

  return Math.max(
    body.scrollHeight || 0,
    body.offsetHeight || 0,
    documentElement?.scrollHeight || 0,
    documentElement?.offsetHeight || 0,
  );
};

const AvailableSyndicationsFrame = () => {
  const iframeRef = useRef(null);
  const measureIframeRef = useRef(null);
  const mutationObserverRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const hasReceivedIframeHeightRef = useRef(false);
  const resizeRequestTimeoutsRef = useRef([]);
  const [pageHtml, setPageHtml] = useState('');
  const [iframeHeight, setIframeHeight] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isPageUnavailable, setIsPageUnavailable] = useState(false);
  const {
    canViewSyndications,
    isError,
    isLoggedIn,
    isUkPerson,
    isLoading,
  } = useSyndicationAccess();

  const setMeasuredIframeHeight = useCallback((height, isVisibleIframeHeight = false) => {
    if (!Number.isFinite(height) || height <= 0) {
      return;
    }

    const nextHeight = Math.ceil(height);
    if (isVisibleIframeHeight) {
      hasReceivedIframeHeightRef.current = true;
    } else if (hasReceivedIframeHeightRef.current) {
      return;
    }

    setIframeHeight((currentHeight) => {
      if (currentHeight === nextHeight) {
        return currentHeight;
      }

      return nextHeight;
    });
  }, []);

  const requestVisibleIframeHeight = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({
      type: IFRAME_MEASURE_MESSAGE_TYPE,
    }, '*');
  }, []);

  const requestVisibleIframeHeightAfterResize = useCallback(() => {
    resizeRequestTimeoutsRef.current.forEach(window.clearTimeout);
    resizeRequestTimeoutsRef.current = [0, 100, 500].map((delay) => (
      window.setTimeout(requestVisibleIframeHeight, delay)
    ));
  }, [requestVisibleIframeHeight]);

  const syncIframeHeight = useCallback(() => {
    const document = measureIframeRef.current?.contentDocument;
    if (!document) {
      return;
    }

    const height = getDocumentHeight(document);
    if (!Number.isFinite(height) || height <= 0) {
      return;
    }

    setMeasuredIframeHeight(height);
  }, [setMeasuredIframeHeight]);

  const syncMeasurementIframe = useCallback(() => {
    const document = measureIframeRef.current?.contentDocument;
    if (!document?.body) {
      syncIframeHeight();
      return;
    }

    mutationObserverRef.current?.disconnect();
    resizeObserverRef.current?.disconnect();

    mutationObserverRef.current = new MutationObserver(syncIframeHeight);
    mutationObserverRef.current.observe(document.body, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });

    resizeObserverRef.current = new ResizeObserver(syncIframeHeight);
    resizeObserverRef.current.observe(document.body);
    if (document.documentElement) {
      resizeObserverRef.current.observe(document.documentElement);
    }

    syncIframeHeight();
  }, [syncIframeHeight]);

  useEffect(() => {
    if (isLoading || !isLoggedIn) {
      return;
    }

    if (isUkPerson) {
      window.location.replace('/');
      return;
    }

    if (isError || !canViewSyndications) {
      window.location.replace(getSyndicateSignupUrl());
    }
  }, [canViewSyndications, isError, isLoading, isLoggedIn, isUkPerson]);

  useEffect(() => {
    if (isLoading || isError || !canViewSyndications) {
      return undefined;
    }

    const abortController = new AbortController();
    let ignore = false;

    async function fetchPage() {
      setIsPageLoading(true);
      setIsPageUnavailable(false);

      try {
        const page = await getAvailableSyndicationsPage(abortController.signal);
        if (ignore || page === 'aborted') {
          return;
        }

        if (!page?.html) {
          throw new Error('Available syndications page response is missing html.');
        }

        setPageHtml(page.html);
      } catch (error) {
        if (ignore || error.name === 'AbortError') {
          return;
        }

        if (error.statusCode === 403) {
          window.location.replace(getSyndicateSignupUrl());
          return;
        }

        setIsPageUnavailable(true);
      } finally {
        if (!ignore) {
          setIsPageLoading(false);
        }
      }
    }

    fetchPage();

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [canViewSyndications, isError, isLoading]);

  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const message = event.data;
      if (!message) {
        return;
      }

      if (message.type === IFRAME_HEIGHT_MESSAGE_TYPE) {
        setMeasuredIframeHeight(message.height, true);
        return;
      }

      if (message.type !== IFRAME_ANALYTICS_MESSAGE_TYPE) {
        return;
      }

      if (!canPushIframeAnalytics()) {
        return;
      }

      window.dataLayer.push({
        event: IFRAME_ANALYTICS_EVENT_NAME,
        available_syndications_tag: normalizeAnalyticsText(
          message.tagName,
          MAX_ANALYTICS_TEXT_LENGTH,
        ).toLowerCase(),
        available_syndications_text: normalizeAnalyticsText(
          message.text,
          MAX_ANALYTICS_TEXT_LENGTH,
        ),
        available_syndications_href: normalizeAnalyticsHref(message.href),
      });
    };

    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [setMeasuredIframeHeight]);

  useEffect(() => {
    if (!isPageUnavailable) {
      return undefined;
    }

    const redirectTimeout = window.setTimeout(() => {
      window.location.replace('/');
    }, DASHBOARD_REDIRECT_DELAY_MS);

    return () => window.clearTimeout(redirectTimeout);
  }, [isPageUnavailable]);

  useEffect(() => {
    if (!pageHtml) {
      return undefined;
    }

    const requestTimeouts = [0, 100, 500, 1000, 2000].map((delay) => (
      window.setTimeout(syncMeasurementIframe, delay)
    ));
    const handleWindowResize = () => {
      requestVisibleIframeHeightAfterResize();
      syncIframeHeight();
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      requestTimeouts.forEach(window.clearTimeout);
      window.removeEventListener('resize', handleWindowResize);
      resizeRequestTimeoutsRef.current.forEach(window.clearTimeout);
      resizeRequestTimeoutsRef.current = [];
      mutationObserverRef.current?.disconnect();
      resizeObserverRef.current?.disconnect();
    };
  }, [pageHtml, requestVisibleIframeHeightAfterResize, syncIframeHeight, syncMeasurementIframe]);

  if (isPageUnavailable) {
    return (
      <Container className="dashboard">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: '320px', textAlign: 'center' }}
        >
          <p style={{ color: '#000', fontSize: '16px', margin: 0 }}>
            {PAGE_UNAVAILABLE_MESSAGE}
          </p>
        </Box>
      </Container>
    );
  }

  if (isLoading || isPageLoading || isError || !canViewSyndications || !pageHtml) {
    return (
      <Container className="dashboard">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: '320px' }}
        >
          <Loader />
        </Box>
      </Container>
    );
  }

  return (
    <Container className="dashboard" style={{ position: 'relative' }}>
      <iframe
        ref={iframeRef}
        title="Available Syndications"
        srcDoc={addIframeScripts(pageHtml)}
        referrerPolicy="strict-origin-when-cross-origin"
        sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox allow-scripts allow-top-navigation-by-user-activation"
        scrolling="no"
        style={{
          width: '100%',
          height: iframeHeight ? `${iframeHeight}px` : 'calc(100vh - 150px)',
          minHeight: 'calc(100vh - 150px)',
          border: 0,
          display: 'block',
          backgroundColor: '#fff',
        }}
      />
      <iframe
        ref={measureIframeRef}
        aria-hidden="true"
        srcDoc={pageHtml}
        onLoad={syncMeasurementIframe}
        sandbox="allow-same-origin"
        scrolling="no"
        tabIndex={-1}
        title="Available Syndications measurement"
        style={{
          width: '100%',
          height: 0,
          border: 0,
          left: 0,
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          visibility: 'hidden',
        }}
      />
    </Container>
  );
};

export default AvailableSyndicationsFrame;
